/**
 * I-1417, access control §26: a passkey ceremony runs only at the origin the deployment verifies it
 * at, which the server names with every set of options.
 *
 * A deployment is reached at more than one address — local stable at `localhost` and at its tailnet
 * name, dev at `localhost` and `127.0.0.1` — and a passkey belongs to one of them. A browser on any
 * other address refuses the ceremony before any prompt, with a `SecurityError` that can only say
 * "different web address". So the page does not start it: a sign-in moves to the same page at the
 * named origin, and a step that needs this address's session says where to go.
 */
export class PasskeyAtAnotherAddressError extends Error {
  override readonly name = 'PasskeyAtAnotherAddress';

  constructor(
    /** This page's path, query and fragment at the ceremony origin. */
    readonly address: string,
    /** The ceremony origin the server named. */
    readonly ceremonyOrigin: string,
  ) {
    super(`Passkeys for this deployment are used at ${ceremonyOrigin}.`);
  }
}

/**
 * This page at the ceremony origin, or null when the page is already there — or when the server named
 * no origin (an older deployment) or something that is not a web origin, in which case the ceremony
 * runs here as it always did and the browser's own refusal is the answer.
 */
export function addressAtCeremonyOrigin(
  ceremonyOrigin: string | undefined,
  here: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>,
): string | null {
  if (!ceremonyOrigin) return null;
  let target: URL;
  try {
    target = new URL(ceremonyOrigin);
  } catch {
    return null;
  }
  if (target.protocol !== 'https:' && target.protocol !== 'http:') return null;
  if (target.origin === here.origin) return null;
  return `${target.origin}${here.pathname}${here.search}${here.hash}`;
}

/** Throws `PasskeyAtAnotherAddressError` when this page is not where the ceremony must run. */
export function assertCeremonyRunsHere(ceremonyOrigin: string | undefined): void {
  const address = addressAtCeremonyOrigin(ceremonyOrigin, window.location);
  if (address) {
    throw new PasskeyAtAnotherAddressError(address, new URL(address).origin);
  }
}

/**
 * A document load to the same page at the ceremony origin — another address of this deployment, so
 * not a page of this app's router (`internalLinks.guard.test.ts`).
 */
export function goToCeremonyOrigin(error: PasskeyAtAnotherAddressError): void {
  window.location.assign(error.address);
}
